/**
 * Tenant Header (epic7 A1) — branded public booking header.
 *
 * Fetches tenant branding for the slug-scoped public booking page and renders a
 * logo + business name + welcome message + today's hours. Applies the tenant
 * primary colour as a CSS custom property and reflects the light/dark theme on
 * the document root. Public + unauthenticated (no PII in the payload).
 *
 *   Endpoint: GET /v1/book/:tenantSlug/branding   (publicE7Routes A1)
 *
 * Design-system: reuses the existing `.ts-booking-header*` tokens so the header
 * is visually consistent with the rest of the public booking surface.
 *
 * Render contract:
 *   renderTenantHeader(target, slug?) -> Promise<TenantBranding | null>
 *     - `target` is the element the header HTML is written into.
 *     - resolves with the branding (so callers can reuse name/theme), or null
 *       when branding could not be loaded (a minimal fallback header is shown).
 *     - returns a teardown handle via the optional 3rd arg pattern is not needed
 *       here (no listeners/timers are attached), but an `abort` controller is
 *       honoured so an in-flight fetch is cancelled on SPA navigation.
 */

// ── Types (mirror TenantBrandingQuery.TenantBranding payload) ───────────

export interface TenantBranding {
  name: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  logo_url: string | null;
  primary_color: string;
  welcome_message: string | null;
  theme: 'light' | 'dark';
  hours_today: string;
  cancellation_policy_text: string;
  /** Canonical business_type id (drives the default banner). */
  business_type: string;
  /** Always a usable banner URL — custom upload or business-type default. */
  banner_url: string;
}

export interface RenderTenantHeaderOptions {
  /** Tenant slug; defaults to the slug parsed from `/book/:slug` in the path. */
  slug?: string;
  /** Optional AbortSignal so an in-flight branding fetch is cancelled on nav. */
  signal?: AbortSignal;
  /** When true, apply primary colour + theme to the document root. Default true. */
  applyTheme?: boolean;
}

// ── Helpers ─────────────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/** Parse the tenant slug from `/book/:slug/...`, falling back to a global config. */
function resolveSlug(explicit?: string): string {
  if (explicit) return explicit;
  const cfg = (window as { __TIMESLOT_CONFIG__?: { tenantSlug?: string } }).__TIMESLOT_CONFIG__;
  if (cfg?.tenantSlug) return cfg.tenantSlug;
  const match = window.location.pathname.match(/^\/book\/([a-z0-9-]+)/);
  return match?.[1] ?? '';
}

/** Hex colour guard — only allow safe `#rrggbb` / `#rgb` to hit the style attr. */
function safeColor(value: string): string | null {
  return /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value) ? value : null;
}

/**
 * Build a safe `tel:` href from a saved phone. Keeps only characters valid in a
 * telephone URI (digits, leading +, and separators) so nothing user-controlled can
 * break out of the attribute; returns null when no usable digits remain.
 */
function safeTelHref(phone: string): string | null {
  const cleaned = phone.replace(/[^0-9+]/g, '');
  const digits = cleaned.replace(/\+/g, '');
  if (digits.length < 3) return null;
  // Collapse any stray '+' to a single leading one.
  const plus = phone.trim().startsWith('+') ? '+' : '';
  return `tel:${plus}${digits}`;
}

/** Basic e-mail shape guard for a `mailto:` link. */
function isEmailish(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

// ── Render fragments ────────────────────────────────────────────────────

function renderHeaderMarkup(branding: TenantBranding): string {
  const logo = branding.logo_url
    ? `<img class="ts-booking-header__logo" src="${escapeHtml(branding.logo_url)}" alt="${escapeHtml(branding.name)} logo" width="56" height="56" loading="lazy" decoding="async">`
    : `<span class="ts-booking-header__logo ts-avatar ts-avatar--md" aria-hidden="true">${escapeHtml(initials(branding.name))}</span>`;

  const tagline = branding.welcome_message
    ? `<p class="ts-booking-header__tagline">${escapeHtml(branding.welcome_message)}</p>`
    : '';

  const hours =
    branding.hours_today && branding.hours_today.trim().length > 0
      ? `<p class="ts-booking-header__hours"><span aria-hidden="true">&#128336;</span> ${escapeHtml(branding.hours_today)}</p>`
      : '';

  // Accessible contact links for the saved phone/email so the values the tenant
  // entered in Branding settings actually surface on the public page (F4b). Each is a
  // real actionable link (tel:/mailto:) with an aria-label; skipped when unset/invalid.
  const contactLinks: string[] = [];
  if (branding.phone) {
    const tel = safeTelHref(branding.phone);
    if (tel) {
      contactLinks.push(
        `<a class="ts-booking-header__contact-link" href="${escapeHtml(tel)}" aria-label="Call ${escapeHtml(branding.name)}"><span aria-hidden="true">&#128222;</span> ${escapeHtml(branding.phone)}</a>`,
      );
    }
  }
  if (branding.email && isEmailish(branding.email)) {
    contactLinks.push(
      `<a class="ts-booking-header__contact-link" href="mailto:${escapeHtml(branding.email)}" aria-label="Email ${escapeHtml(branding.name)}"><span aria-hidden="true">&#9993;</span> ${escapeHtml(branding.email)}</a>`,
    );
  }
  const contact =
    contactLinks.length > 0
      ? `<p class="ts-booking-header__contact">${contactLinks.join(' ')}</p>`
      : '';

  // Decorative hero banner ABOVE the header. alt="" because the business name
  // is already announced in the header below — the banner adds no new info to
  // assistive tech. banner_url is always populated by the query, but guard anyway.
  const banner =
    typeof branding.banner_url === 'string' && branding.banner_url
      ? `<figure class="ts-booking-banner" role="presentation"><img class="ts-booking-banner__img" src="${escapeHtml(branding.banner_url)}" alt="" loading="eager" decoding="async"></figure>`
      : '';

  return `
    ${banner}
    <header class="ts-booking-header" role="banner">
      ${logo}
      <div class="ts-booking-header__meta">
        <p class="ts-booking-header__name">${escapeHtml(branding.name)}</p>
        ${tagline}
        ${hours}
        ${contact}
      </div>
    </header>
  `;
}

function renderFallbackMarkup(): string {
  return `
    <header class="ts-booking-header" role="banner">
      <div class="ts-booking-header__meta">
        <p class="ts-booking-header__name">Book Online</p>
      </div>
    </header>
  `;
}

function renderSkeleton(): string {
  return `
    <div class="ts-booking-header" aria-hidden="true">
      <span class="ts-skeleton ts-skeleton--avatar-md"></span>
      <div class="ts-booking-header__meta">
        <span class="ts-skeleton ts-skeleton--heading"></span>
        <span class="ts-skeleton ts-skeleton--text-sm"></span>
      </div>
    </div>
  `;
}

// ── Theme application ───────────────────────────────────────────────────

function applyBrandTheme(branding: TenantBranding): void {
  const root = document.documentElement;
  const color = safeColor(branding.primary_color);
  if (color) {
    // The booking-page components key off `--ts-color-primary` (+ hover/subtle/muted/
    // active shades), NOT `--ts-primary` — so set the real vars (with derived shades via
    // color-mix) or the tenant's brand colour never reaches the buttons/accents.
    root.style.setProperty('--ts-primary', color);
    root.style.setProperty('--ts-color-primary', color);
    root.style.setProperty('--ts-color-primary-hover', `color-mix(in srgb, ${color} 85%, black)`);
    root.style.setProperty('--ts-color-primary-active', `color-mix(in srgb, ${color} 72%, black)`);
    root.style.setProperty('--ts-color-primary-muted', `color-mix(in srgb, ${color} 30%, white)`);
    root.style.setProperty('--ts-color-primary-subtle', `color-mix(in srgb, ${color} 12%, white)`);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', color);
  }
  root.setAttribute('data-theme', branding.theme === 'dark' ? 'dark' : 'light');
}

// ── Public API ──────────────────────────────────────────────────────────

/**
 * Fetch + render the branded tenant header into `target`.
 * Resolves with the branding payload, or null when it cannot be loaded.
 */
export async function renderTenantHeader(
  target: HTMLElement,
  options: RenderTenantHeaderOptions = {},
): Promise<TenantBranding | null> {
  const slug = resolveSlug(options.slug);
  if (!slug) {
    target.innerHTML = renderFallbackMarkup();
    return null;
  }

  target.innerHTML = renderSkeleton();

  try {
    const init: RequestInit = { headers: { Accept: 'application/json' } };
    if (options.signal) init.signal = options.signal;
    const response = await fetch(`/v1/book/${encodeURIComponent(slug)}/branding`, init);

    if (!response.ok) {
      // 404 / 5xx → minimal, non-broken header (the page can still function).
      target.innerHTML = renderFallbackMarkup();
      return null;
    }

    const branding = (await response.json()) as TenantBranding;
    target.innerHTML = renderHeaderMarkup(branding);
    if (options.applyTheme !== false) {
      applyBrandTheme(branding);
    }
    return branding;
  } catch (err: unknown) {
    // Aborted navigation is not an error worth surfacing.
    if (err instanceof DOMException && err.name === 'AbortError') {
      return null;
    }
    target.innerHTML = renderFallbackMarkup();
    return null;
  }
}
